"use client";

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import FacilityRequestModal from '@/components/common/FacilityRequestModal';
import ClinicianApplicationModal from '@/components/common/ClinicianApplicationModal';

export default function FinalCTASection() {
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [clinicianModalOpen, setClinicianModalOpen] = useState(false);

  return (
    <>
      <section className="relative py-32 bg-gradient-to-br from-[#f8faf9] via-white to-[#f0f4f2] overflow-hidden">
        {/* Enhanced Grid Overlay for This Section */}
        <div 
          className="absolute inset-0 pointer-events-none z-0 backdrop-blur-[5px]"
          style={{
            backgroundImage: 'linear-gradient(#26342f 1.5px, transparent 1.5px), linear-gradient(90deg, #26342f 1.5px, transparent 1.5px)',
            backgroundSize: '60px 60px',
            opacity: 0.03
          }}
        />
        {/* Decorative Top Border */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3a4d46]/20 to-transparent"></div>
        
        {/* Enhanced Background Effects */}
        <div className="absolute inset-0 opacity-[0.06]">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#3a4d46] rounded-full blur-[120px] animate-pulse-glow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#4d6359] rounded-full blur-[100px] animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: 'linear-gradient(#3a4d46 1px, transparent 1px), linear-gradient(90deg, #3a4d46 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }}></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6">
          {/* Main Content Card */}
          <div className="relative p-12 md:p-16 rounded-[3rem] bg-white/80 backdrop-blur-xl border-2 border-[#3a4d46]/20 shadow-2xl">
            {/* Inner Gradient */}
            <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-[#f8faf9]/50 via-transparent to-[#f0f4f2]/50 pointer-events-none"></div>
            
            <div className="relative text-center">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-[#f8faf9] border border-[#3a4d46]/20">
                <div className="w-2 h-2 bg-[#3a4d46] rounded-full animate-pulse"></div>
                <span className="text-sm font-bold text-[#3a4d46] tracking-wider uppercase">Ready to Get Started?</span>
              </div>

              {/* Headline */}
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#26342f] mb-6 leading-tight tracking-tight">
                Ready to hire or ready for your next role?
              </h2>
              
              {/* Subheadline */}
              <p className="text-xl md:text-2xl text-[#3a4d46] mb-12 leading-relaxed max-w-3xl mx-auto">
                Let's get started. Simple process. Fast results. No surprises.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-12">
                <button
                  onClick={() => setFacilityModalOpen(true)}
                  className="group relative inline-flex items-center justify-center px-10 py-5 text-lg font-semibold text-white bg-gradient-to-r from-[#26342f] to-[#3a4d46] rounded-2xl transition-all duration-300 hover:shadow-[0_0_40px_rgba(93,211,158,0.4)] hover:-translate-y-1 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#3a4d46] to-[#4d6359] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative flex items-center gap-2">
                    Request candidates
                    <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </button>
                <button
                  onClick={() => setClinicianModalOpen(true)}
                  className="px-10 py-5 text-lg font-bold text-[#26342f] bg-white border-2 border-[#3a4d46]/30 rounded-2xl hover:bg-[#f8faf9] hover:border-[#3a4d46] transition-all duration-300 hover:shadow-xl hover:-translate-y-1 w-full sm:w-auto"
                >
                  Apply Now
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-8 border-t border-[#3a4d46]/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#f8faf9] to-[#f0f4f2] rounded-lg flex items-center justify-center border border-[#3a4d46]/10">
                    <Icon name="ClockIcon" size={20} className="text-[#3a4d46]" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm text-[#3a4d46]">First candidates in</div>
                    <div className="text-lg font-bold text-[#26342f]">48 hours</div>
                  </div>
                </div>
                <div className="hidden sm:block w-px h-12 bg-[#3a4d46]/20"></div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#f8faf9] to-[#f0f4f2] rounded-lg flex items-center justify-center border border-[#3a4d46]/10">
                    <Icon name="ShieldCheckIcon" size={20} className="text-[#3a4d46]" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm text-[#3a4d46]">Guarantee</div>
                    <div className="text-lg font-bold text-[#26342f]">90 days</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <FacilityRequestModal isOpen={facilityModalOpen} onClose={() => setFacilityModalOpen(false)} />
      <ClinicianApplicationModal isOpen={clinicianModalOpen} onClose={() => setClinicianModalOpen(false)} />
    </>
  );
}